import { Button } from '@fullstory/components';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useFlags } from '@fullstory/react-hooks';
import {
  AppDispatch,
  DashboardRequestActions,
  dashboardsSelectors,
  fullstorySelectors,
} from '@fullstory/redux';
import { getDashboardPath, OVERLAY_QUERY_PARAM } from '@fullstory/routes';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FROM_DASHBOARD_CARD_PARAM, FROM_DASHBOARD_PARAM } from '../utils';

const { getOrgId, getUserInfo } = fullstorySelectors;
const { dashboardsById, cardsById } = dashboardsSelectors;

export const useReturnToDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const dashboards = useSelector(dashboardsById);
  const cards = useSelector(cardsById);
  const { email } = useSelector(getUserInfo);
  const flags = useFlags();

  const location = useLocation();
  const history = useHistory();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const cardId = queryParams.get(FROM_DASHBOARD_CARD_PARAM);
  const dashboardId = queryParams.get(FROM_DASHBOARD_PARAM);
  const dashboardOverlayHash = queryParams.get(OVERLAY_QUERY_PARAM) ?? undefined;

  const returnToDashboard = dashboardId ? dashboards[dashboardId] : null;

  const returnToCard = cardId ? cards[cardId] : null;

  useEffect(() => {
    if (flags.hasBit(Flag.FeatDashboards)) {
      dispatch(
        DashboardRequestActions.fetchAllDashboard(
          { orgId, email, omitSeatSettings: false },
          email,
          flags,
        ),
      );
    }
  }, [dispatch, email, orgId, flags]);

  if (dashboardId && returnToDashboard) {
    return {
      returnToDashboard,
      returnToDashboardButton: (
        <Button
          iconLeft="arrow-left"
          variant="fill-secondary"
          onClick={() =>
            history.push(
              getDashboardPath(
                orgId,
                dashboardId as int64,
                getReturnToDashboardSearch(queryParams),
                dashboardOverlayHash,
              ),
            )
          }
        >
          {returnToDashboard.name}
        </Button>
      ),
    };
  }

  return {
    returnToDashboard: returnToDashboard ?? null,
    returnToDashboardButton: null,
    returnToCard,
    dashboardOverlayHash,
  };
};

const getReturnToDashboardSearch = (queryParams: URLSearchParams) => {
  const returnToDashboardQueryParams = new URLSearchParams();
  const cardId = queryParams.get(FROM_DASHBOARD_CARD_PARAM);
  if (cardId) returnToDashboardQueryParams.set('scrollTo', cardId);

  return returnToDashboardQueryParams.toString();
};

export const getReturnToDashboard = (search: string) => {
  const params = new URLSearchParams(search);
  return params.get(FROM_DASHBOARD_PARAM);
};
